import moment, { Moment } from 'moment';
import { CopyForm } from '../interfaces';

// Define the dataset type
interface Dataset {
  dates: string[];
  weekNumbers: number[];
  daysInWeek: number[];
  values: number[][];
  copyLengthPerWeek: number[];
}

// Define the transformed week type
interface TransformedWeek {
  weekNumber: number;
  startingDate: string;
  numberOfDays: number;
  firstDay: string;
  copy: {
    lengthPerWeek: number;
    value: { id: string; value: number }[];
  };
}

// Define the return type for the transformDataset function
interface TransformedDataset {
  weeks: TransformedWeek[];
  weeklyWeight?: number;
  planTotals: number[];
  grandTotal: number;
  columnTotals: number[];
}

export function calculateDataset(
  startDate: Moment | null,
  endDate: Moment | null
): Dataset {
  if (!startDate || !endDate) {
    console.error('Start date or end date is not set.');
    return {
      dates: [],
      weekNumbers: [],
      daysInWeek: [],
      values: [],
      copyLengthPerWeek: [],
    };
  }

  const startMoment = moment(startDate, 'DD-MM-YYYY', true).startOf('day');
  const endMoment = moment(endDate, 'DD-MM-YYYY', true).endOf('day');

  const totalDays = endMoment.diff(startMoment, 'days') + 1;

  const daysArray = Array.from({ length: totalDays }, (_, i) =>
    startMoment.clone().add(i, 'days')
  );

  const dataset: Dataset = {
    dates: [],
    weekNumbers: [],
    daysInWeek: [],
    values: [],
    copyLengthPerWeek: [],
  };

  const processedWeeks: Set<string> = new Set(); // Track already processed weeks

  daysArray.forEach((currentDate) => {
    const weekStart = currentDate.clone().startOf('isoWeek'); // Start of ISO week (Monday)
    const weekEnd = currentDate.clone().endOf('isoWeek'); // End of ISO week (Sunday)
    const weekNumber = weekStart.isoWeek();
    const weekId = `${weekStart.format('DD-MM-YYYY')}-${weekEnd.format(
      'DD-MM-YYYY'
    )}`;

    if (processedWeeks.has(weekId)) {
      return;
    }
    processedWeeks.add(weekId);

    const constrainedWeekStart = moment.max(weekStart, startMoment);
    const constrainedWeekEnd = moment.min(weekEnd, endMoment);

    const numberOfDays =
      constrainedWeekEnd.diff(constrainedWeekStart, 'days') + 1;

    if (constrainedWeekStart.month() !== constrainedWeekEnd.month()) {
      const firstSegmentEnd = moment.min(
        constrainedWeekStart.clone().endOf('month'),
        constrainedWeekEnd
      );
      const firstSegmentDays =
        firstSegmentEnd.diff(constrainedWeekStart, 'days') + 1;

      dataset.dates.push(constrainedWeekStart.format('DD-MM-YYYY'));
      dataset.weekNumbers.push(weekNumber);
      dataset.daysInWeek.push(firstSegmentDays);
      dataset.values.push(new Array(firstSegmentDays).fill(0));
      dataset.copyLengthPerWeek.push(0);

      const secondSegmentStart = firstSegmentEnd.clone().add(1, 'day');
      const secondSegmentDays =
        constrainedWeekEnd.diff(secondSegmentStart, 'days') + 1;

      dataset.dates.push(secondSegmentStart.format('DD-MM-YYYY'));
      dataset.weekNumbers.push(weekNumber);
      dataset.daysInWeek.push(secondSegmentDays);
      dataset.values.push(new Array(secondSegmentDays).fill(0));
      dataset.copyLengthPerWeek.push(0);
    } else {
      dataset.dates.push(constrainedWeekStart.format('DD-MM-YYYY'));
      dataset.weekNumbers.push(weekNumber);
      dataset.daysInWeek.push(numberOfDays);
      dataset.values.push(new Array(numberOfDays).fill(0));
      dataset.copyLengthPerWeek.push(0);
    }
  });

  return dataset;
}

export function transformDataset(
  startDate: Moment | null,
  endDate: Moment | null,
  copyForms: CopyForm[]
): TransformedDataset {
  const dataset = calculateDataset(startDate, endDate);
  const transformedWeeks: TransformedWeek[] = [];

  dataset.weekNumbers.forEach((weekNumber, index) => {
    const weekStart = moment(dataset.dates[index], 'DD-MM-YYYY', true);
    const numberOfDays = dataset.daysInWeek[index];

    transformedWeeks.push({
      weekNumber,
      startingDate: weekStart.format('DD-MM-YYYY'),
      numberOfDays,
      firstDay: weekStart.format('DD-MM-YYYY'),
      copy: {
        lengthPerWeek: dataset.copyLengthPerWeek[index],
        value: copyForms.map((copyForm, copyIndex) => ({
          id: `${copyForm.copyLength || ''}s${
            copyForm.additionalAdvertisers
              ? `_${copyForm.additionalAdvertisers}`
              : ''
          }${
            copyForm.advertisers?.length
              ? `_${
                  copyForm.advertisers
                    ?.map((advertiser) => `${advertiser.id}`)
                    .join('-') || ''
                }`
              : ''
          }_${copyIndex}`,
          name: `${copyForm.copyLength || ''}s${
            copyForm.additionalAdvertisers
              ? `_${copyForm.additionalAdvertisers}`
              : ''
          }${
            copyForm.advertisers?.length
              ? `_${
                  copyForm.advertisers
                    ?.map((advertiser) => `${advertiser.id}`)
                    .join('-') || ''
                }`
              : ''
          }`,
          value: dataset.values[index][copyIndex] || 0,
        })),
      },
    });
  });

  return {
    weeks: transformedWeeks,
    planTotals: transformedWeeks[0]?.copy?.value.map(() => 0),
    grandTotal: 0,
    columnTotals: transformedWeeks.map(() => 0) || [],
  };
}
